import type { GetChangesResult } from 'components/providers/GetChangesProvider'
import { useEffect, useRef, useState } from 'react'
import { isSyncAuthenticationError } from 'services/api/changes'
import { Account } from 'services/db/Queries/AccountQueries'
import { UserSettings } from 'services/db/Queries/UserSettingsQueries'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'

type StartupState = {
  status: 'loading' | 'ready' | 'failed'
  hasUsableData: boolean
}

async function hasUsableAccount(): Promise<boolean> {
  const [account, settings] = await Promise.all([Account(), UserSettings()])
  const userId = LocalStorage.get(LocalStorageKey.USER_ID)
  return Boolean(
    userId &&
      account?._id === userId &&
      settings?._id &&
      settings.UserID === userId
  )
}

export default function useStartupSession(
  enabled: boolean,
  sync: () => Promise<GetChangesResult>,
  onInvalidSession: () => Promise<void> | undefined
) {
  const [state, setState] = useState<StartupState>({
    status: 'loading',
    hasUsableData: false
  })
  const [attempt, setAttempt] = useState(0)
  const callbacks = useRef({ sync, onInvalidSession })
  useEffect(() => {
    callbacks.current = { sync, onInvalidSession }
  }, [sync, onInvalidSession])
  // Reuse the same attempt through React's effect setup/cleanup replay.
  const pending = useRef<Promise<
    'ready' | 'invalid' | 'failed' | 'cached'
  > | null>(null)

  useEffect(() => {
    let active = true
    if (!enabled) {
      pending.current = null
      Promise.resolve().then(() => {
        if (active) setState({ status: 'loading', hasUsableData: false })
      })
      return () => {
        active = false
      }
    }

    const userId = LocalStorage.get(LocalStorageKey.USER_ID)
    if (!pending.current) {
      pending.current = (async () => {
        try {
          const result = await callbacks.current.sync()
          if (!result.success && isSyncAuthenticationError(result.error)) {
            return 'invalid'
          }
          const usable = await hasUsableAccount()
          if (result.success) return usable ? 'ready' : 'invalid'
          return usable ? 'cached' : 'failed'
        } catch {
          // A database/setup failure is retryable; it is not proof of bad credentials.
          return 'failed'
        }
      })()
    }

    const task = pending.current
    task.then(async (outcome) => {
      if (!active) return
      // Never log out a different account that another window has just signed in.
      if (userId && userId !== LocalStorage.get(LocalStorageKey.USER_ID)) {
        setState({ status: 'failed', hasUsableData: false })
        return
      }
      if (outcome === 'invalid') {
        try {
          await callbacks.current.onInvalidSession()
        } catch {
          if (active) setState({ status: 'failed', hasUsableData: false })
        }
      } else {
        setState({
          status: outcome === 'ready' ? 'ready' : 'failed',
          hasUsableData: outcome === 'ready' || outcome === 'cached'
        })
      }
    })
    return () => {
      active = false
    }
  }, [enabled, attempt])

  return {
    ...state,
    retrying: state.status === 'loading' && attempt > 0,
    retry: () => {
      if (state.status !== 'failed') return
      pending.current = null
      setState({ status: 'loading', hasUsableData: state.hasUsableData })
      setAttempt((value) => value + 1)
    },
    dismissError: () => {
      if (state.hasUsableData)
        setState({ status: 'ready', hasUsableData: true })
    }
  }
}
